import React, { JSX, memo, useState } from 'react';
import { PATHS } from 'src/common/constants';
import { variables } from 'src/Tenants';
import { onRequestPhoneCall } from 'src/helpers/validationHelper';
import Tenant from 'src/components/Tenant';
import Card from 'src/components/Card';
import { Link } from 'react-router-dom';
import Avatar from 'src/components/Avatar';
import Metrics from 'src/components/Metrics';
import FeedBack from 'src/components/FeedBack';
import moment from 'moment';
import KeyDates from 'src/pages/Suite/Components/KeyDates/KeyDates';

interface HomeOwnerInfoProps {
  suite: {
    homeOwners?: {
      id: string;
      firstname: string;
      lastname: string;
      phone: string;
    };
  };
  hasLinkViewPermission: boolean;
  isFromDashboard: boolean;
}

const HomeOwnerInfo = memo(
  ({ suite, hasLinkViewPermission, isFromDashboard }: HomeOwnerInfoProps) => {
    const fullName = `${suite.homeOwners.firstname} ${suite.homeOwners.lastname}`;

    return (
      <Card
        title={variables.homeOwner}
        body={
          <Tenant
            headline={
              hasLinkViewPermission ? (
                <Link
                  to={{
                    pathname: PATHS.CONTACTS_VIEW.replace(
                      ':id',
                      suite.homeOwners.id
                    ),
                    state: { isFromDashBoard: isFromDashboard },
                  }}
                >
                  {fullName}
                </Link>
              ) : (
                fullName
              )
            }
            strapline={onRequestPhoneCall(suite.homeOwners.phone)}
            avatar={<Avatar url={'/assets/banner-property.png'} />}
          />
        }
      />
    );
  }
);

HomeOwnerInfo.displayName = 'HomeOwnerInfo';

interface KeysAndFobsInfoProps {
  suite: {
    keyFobs?: Array<{ name: string }>;
    numberOfKeys?: number;
  };
}

const KeysAndFobsInfo = memo(({ suite }: KeysAndFobsInfoProps) => {
  const formatKeyFobs = () =>
    suite.keyFobs?.length
      ? suite.keyFobs.map(
          (keyFob, index) =>
            keyFob.name + (index === suite.keyFobs.length - 1 ? '' : ', ')
        )
      : 'No key fobs';

  return (
    <Card
      body={
        <div style={{ marginLeft: -10 }}>
          <Tenant
            headline={'KEY FOBS'}
            strapline={<div style={{ padding: 2 }}>{formatKeyFobs()}</div>}
          />
          <br />
          <Tenant
            headline={'PHYSICAL KEYS'}
            strapline={<div style={{ padding: 2 }}>{suite.numberOfKeys}</div>}
          />
        </div>
      }
    />
  );
});

KeysAndFobsInfo.displayName = 'KeysAndFobsInfo';

interface ParkingStorageInfoProps {
  suite: {
    parkingStalls?: Array<{ name: string }>;
    storageLockers?: Array<{ name: string }>;
  };
}

const ParkingStorageInfo = memo(({ suite }: ParkingStorageInfoProps) => {
  const formatList = (items: Array<{ name: string }>) =>
    items.map(
      (item, index) => item.name + (index === items.length - 1 ? '' : ', ')
    );

  return (
    <Card
      body={
        <div style={{ marginLeft: -10 }}>
          <Tenant
            headline={'PARKING STALLS'}
            strapline={
              <div style={{ padding: 2 }}>
                {suite.parkingStalls?.length
                  ? formatList(suite.parkingStalls)
                  : 'No parking slots.'}
              </div>
            }
          />
          <br />
          <Tenant
            headline={'STORAGE LOCKERS'}
            strapline={
              <div style={{ padding: 2 }}>
                {suite.storageLockers?.length
                  ? formatList(suite.storageLockers)
                  : 'No storage lockers.'}
              </div>
            }
          />
        </div>
      }
    />
  );
});

ParkingStorageInfo.displayName = 'ParkingStorageInfo';

interface ServiceRequestMetricsProps {
  counts: {
    serviceActivesCount?: number;
    serviceArchivedCount?: number;
  } | null;
}

const ServiceRequestMetrics = memo(({ counts }: ServiceRequestMetricsProps) => (
  <Metrics
    items={[
      {
        key: 'Active',
        value: counts?.serviceActivesCount || 0,
      },
      {
        key: 'Closed',
        value: counts?.serviceArchivedCount || 0,
      },
    ]}
    title={undefined}
  />
));

ServiceRequestMetrics.displayName = 'ServiceRequestMetrics';

interface SparkMoreDetailsProps {
  suite: {
    isSpark?: boolean;
  };
  onFetchMoreDetails: () => void;
}

const SparkMoreDetails = memo(
  ({ suite, onFetchMoreDetails }: SparkMoreDetailsProps) => {
    return (
      <Card
        body={
          <div>
            <div
              style={{
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={onFetchMoreDetails}
            >
              More Details
            </div>
          </div>
        }
      />
    );
  }
);

SparkMoreDetails.displayName = 'SparkMoreDetails';

interface WarrantyDetailsSectionProps {
  suite: {
    assetWarrantyDefaultStartDate?: string;
    assetWarrantyOneYear?: string;
    assetWarrantyTwoYear?: string;
    warrantyAddress?: string;
  };
  currentTheme: string | null;
  oneYearWarrantyDaysRemaining: number;
  twoYearWarrantyDaysRemaining: number;
  isHomeOwnerUser?: boolean;
}

const WarrantyDetailsSection = memo(
  ({
    suite,
    currentTheme,
    oneYearWarrantyDaysRemaining,
    twoYearWarrantyDaysRemaining,
    isHomeOwnerUser = false,
  }: WarrantyDetailsSectionProps) => {
    const formatWarrantyDate = (date: string, isOneYear: boolean) => {
      if (!date) return '';

      if (currentTheme === 'Albaster') {
        const daysRemaining: number = isOneYear
          ? oneYearWarrantyDaysRemaining
          : twoYearWarrantyDaysRemaining;
        return `${daysRemaining < 0 ? 0 : daysRemaining} remaining days`;
      }

      return isHomeOwnerUser
        ? moment.utc(date).subtract(1, 'days').format('MM/DD/YYYY')
        : date;
    };

    const warrantyStartDate =
      isHomeOwnerUser && suite.assetWarrantyDefaultStartDate
        ? moment(suite.assetWarrantyDefaultStartDate)
            .utc(false)
            .format('MM/DD/YYYY')
        : (suite.assetWarrantyDefaultStartDate ?? '');

    const metricKeyStyle = {
      paddingTop: 5,
      paddingBottom: 5,
      display: 'grid' as const,
    };

    const shouldShowWarrantyAddress =
      isHomeOwnerUser || suite.warrantyAddress !== 'Add';

    return (
      <>
        <Metrics title="warranty details" items={[]} />
        {suite.assetWarrantyDefaultStartDate && (
          <div className="metrics__key" style={metricKeyStyle}>
            <div>Warranty start date : {warrantyStartDate}</div>
          </div>
        )}
        <div className="metrics__key" style={metricKeyStyle}>
          <div>
            1 Year Warranty :{' '}
            {formatWarrantyDate(suite.assetWarrantyOneYear || '', true)}
          </div>
        </div>
        <div className="metrics__key" style={metricKeyStyle}>
          <div>
            2 Year Warranty :{' '}
            {formatWarrantyDate(suite.assetWarrantyTwoYear || '', false)}
          </div>
        </div>
        {shouldShowWarrantyAddress && (
          <div className="metrics__key" style={metricKeyStyle}>
            <div>Warranty Address</div>
            <div style={{ fontSize: 10 }}>{suite.warrantyAddress || ''}</div>
          </div>
        )}
      </>
    );
  }
);

WarrantyDetailsSection.displayName = 'WarrantyDetailsSection';

interface SuiteDetailsComponentsProps {
  suite: any;
  counts: {
    serviceActivesCount?: number;
    serviceArchivedCount?: number;
  } | null;
  currentTheme: string | null;
  oneYearWarrantyDaysRemaining: number;
  twoYearWarrantyDaysRemaining: number;
  isHomeOwnerUser: boolean;
  hasLinkViewPermission: boolean;
  isFromDashboard: boolean;
  feedBackReportPopup: boolean;
  onFetchMoreDetails: () => void;
  refreshKeyDatesTrigger?: number;
  showKeyDates?: boolean;
  onKeyDatesEmpty?: () => void;
}

export const SuiteDetailsComponents = ({
  suite,
  counts,
  currentTheme,
  oneYearWarrantyDaysRemaining,
  twoYearWarrantyDaysRemaining,
  isHomeOwnerUser,
  hasLinkViewPermission,
  isFromDashboard,
  feedBackReportPopup,
  onFetchMoreDetails,
  refreshKeyDatesTrigger,
  showKeyDates = true,
  onKeyDatesEmpty,
}: SuiteDetailsComponentsProps) => {
  const components: JSX.Element[] = [];

  if (feedBackReportPopup) {
    components.push(<FeedBack key="feedback" />);
  }

  const hasWarrantyData =
    suite.assetWarrantyDefaultStartDate ||
    suite.assetWarrantyOneYear ||
    suite.assetWarrantyTwoYear ||
    (suite.warrantyAddress && suite.warrantyAddress !== 'Add');

  if (hasWarrantyData) {
    components.push(
      <WarrantyDetailsSection
        key="warranty"
        suite={suite}
        currentTheme={currentTheme}
        oneYearWarrantyDaysRemaining={oneYearWarrantyDaysRemaining}
        twoYearWarrantyDaysRemaining={twoYearWarrantyDaysRemaining}
        isHomeOwnerUser={isHomeOwnerUser}
      />
    );
  }

  components.push(
    <ServiceRequestMetrics key="service-metrics" counts={counts} />
  );

  if (showKeyDates) {
    components.push(
      <KeyDates
        key="key-dates"
        suiteId={suite.id}
        refreshTrigger={refreshKeyDatesTrigger}
        onEmpty={onKeyDatesEmpty}
      />
    );
  }

  if (isHomeOwnerUser && suite.homeOwners?.id) {
    components.push(
      <HomeOwnerInfo
        key="homeowner-info"
        suite={suite}
        hasLinkViewPermission={hasLinkViewPermission}
        isFromDashboard={isFromDashboard}
      />
    );
  }

  // Only add parking/storage and keys/fobs if not home owner
  if (!isHomeOwnerUser) {
    const hasParkingStalls: boolean =
      suite.parkingStalls && suite.parkingStalls.length > 0;
    const hasStorageLockers: boolean =
      suite.storageLockers && suite.storageLockers.length > 0;

    if (hasParkingStalls || hasStorageLockers) {
      components.push(
        <ParkingStorageInfo key="parking-storage" suite={suite} />
      );
    }

    if (
      suite.keyFobs?.length ||
      (suite.numberOfKeys && suite.numberOfKeys > 0)
    ) {
      components.push(<KeysAndFobsInfo key="keys-fobs" suite={suite} />);
    }
  }

  if (suite.isSpark) {
    components.push(
      <SparkMoreDetails
        key="spark-details"
        suite={suite}
        onFetchMoreDetails={onFetchMoreDetails}
      />
    );
  }

  return components;
};
